import { defineStore } from "pinia";
import { isEqual, snakeCase } from "lodash-es";
import { fetchJson } from "~/recipal-app/controllers/shared/requests";

const DEFAULT_PER_PAGE = 25;

/**
 * How each filter type turns state into query params and back.
 *
 * `blank` builds the value a filter holds when unset, which is also the default it
 * is compared against. It is a function so the array types hand out a new instance
 * per store and per reset rather than sharing one.
 *
 * | type          | blank value | derived param      | sent as                      |
 * | ------------- | ----------- | ------------------ | ---------------------------- |
 * | `string`      | `""`        | `q`                | omitted while blank          |
 * | `date`        | `null`      | `begin_date`       | omitted while blank          |
 * | `stringArray` | `[]`        | `states[]`         | one entry per value          |
 * | `idArray`     | `[]`        | `supplier_ids[]`   | one entry per value, read back as numbers |
 */
const FILTER_TYPES = {
  string: {
    blank: () => "",
    paramFor: (key) => snakeCase(key),
    read: (params, param) => params.get(param),
    write: (params, param, value) => value && params.append(param, value),
  },
  date: {
    blank: () => null,
    paramFor: (key) => snakeCase(key),
    read: (params, param) => params.get(param),
    write: (params, param, value) => value && params.append(param, value),
  },
  stringArray: {
    blank: () => [],
    paramFor: (key) => `${snakeCase(key)}[]`,
    read: (params, param) => params.getAll(param),
    write: (params, param, values) => values.forEach((value) => params.append(param, value)),
  },
  idArray: {
    blank: () => [],
    paramFor: (key) => `${snakeCase(key)}[]`,
    read: (params, param) => params.getAll(param).map(Number),
    write: (params, param, values) => values.forEach((value) => params.append(param, value)),
  },
};

/**
 * @typedef {Object} FilterDescriptor
 * @property {string} key - camelCase state property the filter lives on, e.g. `supplierIds`
 * @property {"string"|"date"|"stringArray"|"idArray"} type - see FILTER_TYPES
 * @property {string} [param] - query param name; defaults to the snake_cased key,
 *   suffixed with `[]` for the array types
 * @property {boolean} [persist=false] - save the value in localStorage for use when
 *   the URL has no params for this list (see use-search-list)
 */

/**
 * Builds a Pinia store for a paginated, filterable index endpoint whose state
 * round-trips through the URL (see use-search-store-history).
 *
 * The endpoint is requested as `${path(userId)}.json` and must answer with
 * `{ [itemsKey], total_entries, per_page, current_page }` -- the shape a Rails
 * FilterService `paginated` already returns.
 *
 * Consumers must call `initSortConfig` with the server-rendered sort config
 * before the first load, since it supplies the default sort.
 *
 * @param {Object} config
 * @param {string} config.id - Pinia store id; also keys the localStorage entry,
 *   so it has to be unique per list
 * @param {(userId: number) => string} config.path - builds the index path, usually
 *   a js-routes helper such as `userPurchaseOrdersPath`
 * @param {string} [config.itemsKey="items"] - names both the state property holding
 *   the rows and the key they arrive under in the JSON response
 * @param {number} [config.perPage=25] - starting page size, until the server reports its own
 * @param {FilterDescriptor[]} [config.filters=[]] - the filters this list supports
 * @returns {Function} a `useStore` hook whose store exposes the filter keys plus
 *   `sortBy`, `sortDirection`, `currentPage`, `perPage`, `totalEntries`, `loaded`,
 *   `userId`, `hasActiveFilters`, and actions `loadPage`, `hydrate`, `setUserId`,
 *   `dropUnknownIds`, `setFilter`, `setFilters`, `setSort`, `toggleSort`,
 *   `setFromSearchParams`, and `resetSearch`
 *
 * @example
 * export const useShipmentSearchStore = createSearchListStore({
 *   id: "shipmentSearch",
 *   path: userShipmentsPath,
 *   itemsKey: "shipments",
 *   filters: [
 *     { key: "q", type: "string" },
 *     { key: "states", type: "stringArray" },       // states[]
 *     { key: "receiverIds", type: "idArray" },      // receiver_ids[]
 *     { key: "shippedBeginDate", type: "date" },    // shipped_begin_date
 *     { key: "shippedEndDate", type: "date" },      // shipped_end_date
 *   ],
 * });
 *
 * // in a component
 * store.setFilter("states", ["draft"]);                             // one filter, one reload
 * store.setFilters({ shippedBeginDate: from, shippedEndDate: to }); // a range, still one reload
 */
export function createSearchListStore({
  id,
  path,
  itemsKey = "items",
  perPage = DEFAULT_PER_PAGE,
  filters = [],
}) {
  const specs = filters.map(({ key, type, param, persist = false }) => {
    const spec = FILTER_TYPES[type];
    if (!spec) throw new Error(`Unknown filter type "${type}" for "${key}"`);

    return { ...spec, key, persist, param: param ?? spec.paramFor(key) };
  });

  const persistedKeys = specs.filter(({ persist }) => persist).map(({ key }) => key);

  const filterKeys = new Set(specs.map(({ key }) => key));
  const blankFilters = () => Object.fromEntries(specs.map(({ key, blank }) => [key, blank()]));

  /* The baseline configuredFilterKeys diffs against, so only state that differs
   * from this reaches the URL. Mutated by initSortConfig once the server-provided
   * sort is known, so that sort does not read as a user choice. */
  const defaults = {
    loaded: false,
    userId: null,
    sortBy: null,
    sortDirection: null,
    [itemsKey]: [],
    totalEntries: 0,
    currentPage: 1,
    perPage,
    ...blankFilters(),
  };

  /* Rebuilds the arrays so state never holds the same instances as `defaults`,
   * where mutating one in place would rewrite the baseline it is compared against. */
  const freshDefaults = () => ({ ...defaults, [itemsKey]: [], ...blankFilters() });

  const queryParamMap = {
    ...Object.fromEntries(specs.map(({ key, param }) => [key, param])),
    sortBy: "sort_by",
    sortDirection: "sort_direction",
    currentPage: "page",
    perPage: "per_page",
    userId: "user_id",
  };

  let sortConfig = {};

  return defineStore(id, {
    persist: {
      storage: localStorage,
      paths: ["userId", ...persistedKeys, "sortBy", "sortDirection"],
    },

    state: freshDefaults,

    getters: {
      queryParamMap: () => queryParamMap,

      configuredFilterKeys: (state) => {
        const diff = Object.keys(queryParamMap)
          .filter((k) => !isEqual(state[k], defaults[k]))
          .map((k) => [k, [state[k], defaults[k]]]);
        return Object.fromEntries(diff);
      },

      /* Narrower than configuredFilterKeys, which also reports sort and pagination. */
      hasActiveFilters: (state) => specs.some(({ key }) => !isEqual(state[key], defaults[key])),

      requestQueryParams: (state) => {
        const params = new URLSearchParams();

        params.append("sort_by", state.sortBy);
        params.append("sort_direction", state.sortDirection);
        params.append("per_page", state.perPage);
        params.append("page", state.currentPage);

        specs.forEach(({ key, param, write }) => write(params, param, state[key]));

        return params;
      },

      requestQueryString() {
        return this.requestQueryParams.toString();
      },
    },

    actions: {
      initSortConfig(config) {
        sortConfig = config;
        defaults.sortBy = config.default_sort_by;
        defaults.sortDirection = config.default_sort_direction;
        if (!this.sortBy) this.sortBy = config.default_sort_by;
        if (!this.sortDirection) this.sortDirection = config.default_sort_direction;
      },

      setUserId(userId) {
        if (this.userId !== null && this.userId !== userId) this.$patch(freshDefaults());
        this.userId = userId;
      },

      dropUnknownIds(knownIds) {
        Object.entries(knownIds).forEach(([key, ids]) => {
          this[key] = this[key].filter((id) => ids.includes(id));
        });
      },

      hydrate(json) {
        this.$patch({
          loaded: true,
          [itemsKey]: json[itemsKey],
          totalEntries: json.total_entries,
          perPage: json.per_page,
          currentPage: json.current_page,
        });
      },

      async loadPage(newPage) {
        if (newPage !== undefined) this.currentPage = newPage;

        const url = `${path(this.userId)}.json?${this.requestQueryString}`;
        const { data: json } = await fetchJson(url);

        this.hydrate(json);
      },

      setFilter(key, value) {
        this.setFilters({ [key]: value });
      },

      setFilters(values) {
        Object.keys(values).forEach((key) => {
          if (!filterKeys.has(key)) throw new Error(`Unknown filter "${key}" for store "${id}"`);
        });

        this.$patch({ ...values, currentPage: 1 });
        this.loadPage();
      },

      setSort(sortBy, sortDirection) {
        const validKeys = sortConfig.options.map((o) => o.key);
        if (!validKeys.includes(sortBy)) sortBy = sortConfig.default_sort_by;
        this.sortBy = sortBy;
        if (sortDirection !== undefined) this.sortDirection = sortDirection;
        this.loadPage(1);
      },

      toggleSort(field) {
        if (this.sortBy === field) {
          this.setSort(field, this.sortDirection === "desc" ? "asc" : "desc");
        } else {
          this.setSort(field, "asc");
        }
      },

      applyFromSearchParams(params) {
        specs.forEach(({ key, param, read }) => {
          this[key] = params.has(param) ? read(params, param) : defaults[key];
        });

        this.sortBy = params.has("sort_by") ? params.get("sort_by") : defaults.sortBy;
        this.sortDirection = params.has("sort_direction")
          ? params.get("sort_direction")
          : defaults.sortDirection;
        this.currentPage = params.has("page") ? Number(params.get("page")) : defaults.currentPage;
      },

      setFromSearchParams(params) {
        this.applyFromSearchParams(params);
        this.loadPage(this.currentPage);
      },

      resetSearch() {
        const { userId } = this;
        this.$patch({ ...freshDefaults(), userId });
        this.loadPage(1);
      },
    },
  });
}
